import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone, MessageCircle, ArrowDown } from "lucide-react";
import { SITE } from "@/data/site";

const LINES = ["CHIPTUNING", "& SCHLÜSSEL-", "DIENST"];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const line = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.42, 0.8]);

  return (
    <section ref={ref} className="relative min-h-[100svh] w-full overflow-hidden">
      {/* Parallax background image */}
      <motion.div style={{ y: imgY }} className="absolute inset-0 z-0">
        <img
          src="https://images.pexels.com/photos/38570/lamborghini-car-speed-prestige-38570.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1600"
          alt="Sportwagen Chiptuning Cloppenburg"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-0 bg-black" />
      <div className="absolute inset-0 z-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
      <div className="absolute inset-0 z-0 radial-glow" />

      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-end px-5 pb-20 pt-32 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="mb-6 flex items-center gap-3 font-body text-[11px] font-bold uppercase tracking-[0.3em] text-volt"
        >
          <span className="h-px w-10 bg-volt" />
          Mobil im Raum {SITE.address.city} · 24/7
        </motion.p>

        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          className="font-heading font-black uppercase leading-[0.85] tracking-tight text-white"
          style={{ fontSize: "clamp(3rem, 11vw, 10rem)" }}
        >
          {LINES.map((l, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span variants={line} className="block">
                {i === 1 || i === 2 ? <span className="text-volt">{l}</span> : l}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-8 max-w-xl font-body text-base text-neutral-300 sm:text-lg"
        >
          Mehr Leistung aus deinem Motor holen oder ausgesperrt am Straßenrand –
          KeyMaster ist dein Spezialist für Chiptuning, KFZ-Elektronik und
          Autoschlüssel. Schnell, mobil und fair.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
        >
          <a
            href={`tel:${SITE.phoneIntl}`}
            data-testid="hero-call-btn"
            className="inline-flex items-center justify-center gap-2 bg-volt px-8 py-4 font-body text-sm font-bold uppercase tracking-wide text-black transition-transform hover:scale-[1.03] glow-volt"
          >
            <Phone className="h-5 w-5" /> {SITE.phoneDisplay}
          </a>
          <a
            href={SITE.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-btn"
            className="inline-flex items-center justify-center gap-2 border border-white/20 px-8 py-4 font-body text-sm font-bold uppercase tracking-wide text-white transition-colors hover:border-volt hover:text-volt"
          >
            <MessageCircle className="h-5 w-5" /> WhatsApp Anfrage
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 text-neutral-500"
      >
        <ArrowDown className="h-5 w-5 animate-bounce" />
      </motion.div>
    </section>
  );
}
